import { ChevronRightIcon } from '@heroicons/react/20/solid';
import * as React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { NotTranslated, T } from '../../../Translations';
import { CommunicationEntry } from './Types';

interface Props {
  collectionName: string;
  communications: CommunicationEntry[];
}

const customizedCount = (communications: CommunicationEntry[]): number =>
  communications.filter((communication) => communication.effectiveTier === 'collection').length;

// Wears the same primary colour as the "This collection" badge in the list, so
// the count and the rows it refers to read as one idea.
const CustomizedPill: React.FC<{ count: number }> = ({ count }) =>
  count === 0 ? (
    <span
      className="inline-flex items-center px-2.5 py-0.5 rounded-full bg-muted text-muted-foreground whitespace-nowrap"
      style={{ fontSize: 'var(--text-xs)', fontWeight: 'var(--font-weight-medium)' }}
    >
      <T kind="No customizations yet" />
    </span>
  ) : (
    <span
      className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-primary text-primary-foreground whitespace-nowrap"
      style={{ fontSize: 'var(--text-xs)', fontWeight: 'var(--font-weight-medium)' }}
    >
      <span>{count}</span>
      <span>
        <T kind="customized" />
      </span>
    </span>
  );

const Header: React.FC<Props> = ({ collectionName, communications }) => (
  <div className="bg-card border-b border-border">
    <div className="max-w-clientPortalMaxWidth mx-auto px-6 md:px-14 py-5">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div className="min-w-0">
          {/* Admins arrive from the collection list, so the crumbs lead back
              there and the collection name is context, not a destination. */}
          <nav
            className="flex items-center gap-1 text-muted-foreground mb-1.5"
            style={{ fontSize: 'var(--text-sm)' }}
            aria-label="Breadcrumb"
          >
            <RouterLink
              data-intercom-target="collection-communications-breadcrumb-link"
              to="/learning_collections"
              className="hover:text-foreground transition-colors"
            >
              <T kind="Learning Collections" />
            </RouterLink>
            <ChevronRightIcon className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
            <span className="truncate text-foreground">
              <NotTranslated text={collectionName} />
            </span>
          </nav>

          <div className="flex items-center gap-3 min-w-0">
            <h1
              className="text-foreground"
              style={{ fontSize: 'var(--text-3xl)', fontWeight: 'var(--font-weight-semibold)' }}
            >
              <T kind="Communications" />
            </h1>
            <CustomizedPill count={customizedCount(communications)} />
          </div>

          <p className="text-muted-foreground mt-1" style={{ fontSize: 'var(--text-sm)' }}>
            <T kind="Choose how participants in this collection are emailed. Anything you customize here overrides your organization's template." />
          </p>
        </div>

        <div className="flex items-center gap-3 flex-shrink-0">
          <RouterLink
            to="/learning_collections"
            className="px-4 py-2 bg-background text-foreground border border-border rounded-md transition-all hover:bg-muted whitespace-nowrap"
            style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--font-weight-medium)' }}
            data-intercom-target="collection-communications-back-to-collections"
          >
            <T kind="Back to Collections" />
          </RouterLink>
        </div>
      </div>
    </div>
  </div>
);

export default Header;
